/**
 *  Module for global app state, components, and the main user interface
 */

const AppUtils = function (app) {
  const $ = require('jquery');
  const _ = require('lodash');
  const configure = app.configure;
  const $container = app.$container;
  // Fix the markup that isn't always complete and add a nonbreaking space
  // to the paragraphs to avoid widows
  function typography(copy) {
    const markup = $.parseHTML(copy),
      nodes = [];

    // Markup is an array of HTML elements found in the copy const
    markup.forEach(function (el) {
      let value = el.outerHTML || '';

      // Split the string on each space and return an array
      value = value.split(' ');

      // Add a non-breaking space as the second to last item of the array
      value.splice((value.length - 1), 0, '&nbsp;');

      // Now joing the value const on a space character
      // and remove the spaces around the non-breaking space
      value = value.join(' ').replace(' &nbsp; ', '&nbsp;');

      nodes.push(value);
    });

    // Send back our modified HTML string
    return nodes.join('');
  }

  // When loading a recipe ensure it is valid, if not
  // allow the UI to display the out of stock dialog
  function isRecipeValid(err, callback) {
    if (err) {
      if (err.fcErrorType === 'recipe:invalid' && err.fcErrorDetails) {
        const unavailable = err.fcErrorDetails.unavailable;

        configure.run('setFallbackRecipe', err.fcErrorDetails.fallbackRecipe, function (err) {
          if (err) { throw err; }

          callback(unavailable);
        });
      } else {
        throw err;
      }
    } else {
      callback({});
    }
  }

  function isNonClothingProduct() {
    return app.category.toLowerCase() === 'non-clothing';
  }

  function isCustomizeItProduct() {
    const blankProjectNames = ['pride', 'denim', 'pink pony', 'print and embroidery', 'olympic polo'];
    return blankProjectNames.includes(app.projectName.toLowerCase());
  }

  function isPrintAndEmbroideryProduct() {
    const blankProjectNames = ['print and embroidery'];
    return blankProjectNames.includes(app.projectName.toLowerCase());
  }

  function isDenimProduct() {
    return app.product.vendorId.toLowerCase().indexOf('denim') >= 0;
  }

  const DTG_CATEGORIES = [
    'Direct to Garment',
    'China DTG',
    'JP DTG',
    'RL Mobile App DTG'
  ];
  function isDTGProduct() {
    return DTG_CATEGORIES.indexOf(app.category) !== -1;
  }

  function isSimpleCYO() {
    return app.projectName === 'Simple CYO';
  }

  function isCampaign() {
    return app.utils.getFirstProductFacet('Campaign', app.product);
  }

  function isDefaultRecipe() {
    let isDefault = false;

    // RAL passes us the default Recipe ID via Configure params. They also
    // have a custom URL parameter they made called fluidRecipeId. When
    // this exists it is an edit cart state and not the defalut ID
    if (
      window.configureParams &&
      window.configureParams.recipeId &&
      !app.mergedParams.fluidRecipeId
    ) {
      isDefault = true;
    }

    return isDefault;
  }

  const isJapan = () => app.mergedParams.locale === 'ja_JP';

  const hasAttribute = (alias) => {
    return _.filter(app.attributes, { alias }).length > 0;
  };

  const hasUiAlias = (alias) => {
    return `${(hasAttribute(`${alias}_ui`) ? `${alias}_ui` : alias)}`;
  };

  // When the param color=1234 is passed we need to select the correct color
  // assuming it doesn't match the default value
  function setColor(color) {
    const styleNumber = configure.run('getProduct').vendorId,
      colorway = configure.run('getAttribute', { alias: 'colorway' }),
      selected = _.find(colorway.values, { selected: true }) || {},
      newValue = _.find(colorway.values, { vendorId: styleNumber + '-' + color }) || {};

    if (!_.isEmpty(newValue) && selected.vendorId !== newValue.vendorId) {
      configure.run('selectValue', {
        caId: colorway.id,
        avId: newValue.id
      }, app._callback);
    }
  }
  // Get a list of selected attribute values for all relevant attributes
  function getSelectedValues() {
    const attributes = app.appUtils.getDataAttributes(),
      selectedValues = {};

    _.forEach(attributes, function (attribute) {
      const selectedValue = _.head(_.filter(attribute.values, { selected: true }));
      if (!_.isUndefined(selectedValue)) {
        selectedValues[attribute.alias] = selectedValue;
      }
    });

    return selectedValues;
  }

  // Function that looks at the image pixel data and
  // determines if the image is all white pixels.
  function checkPixelColor(data, callback) {
    // Create a new image and a canvas element
    // with a 2d context
    const image = new Image(),
      canvas = document.createElement('canvas'),
      ctx = canvas.getContext('2d'),
      viewName = app.utils.getUrlParam('view', data.src);

    canvas.height = data.height;
    canvas.width = data.width;

    // Set the cross origin to anon to prevent loading issues
    image.crossOrigin = 'anonymous';
    // Set the image source to the thumbnail source
    image.src = data.src;
    // When the image loads
    image.onload = function () {
      // Draw the image to the top left of the canvas
      ctx.drawImage(image, 0, 0);

      // Get the RGBA data for all pixels of the image
      const pixel = ctx.getImageData(0, 0, data.width, data.height).data,
        // Remove all duplicates and get the length
        uniqColors = _.remove(_.uniq(pixel), function (n) { return n; }),
        len = uniqColors.length;

      // The pixel data is returned as RGBA in a flat array
      // [R,G,B,A,R,G,B,A,...], A will always = 255 in JPG
      // Blank images will have 2 length.
      // The grey color and the alpha
      callback((len < 10), viewName);
    };
  }

  // Takes an object of attributes and turns it into an array of attributes
  // with the standard {ca: {}, av: {}} structure
  function transformObj(obj) {
    const array = [];

    if (_.isObject(obj)) {
      _.forEach(obj, function (item) {
        array.push({
          ca: item,
          av: _.find(item.values, { selected: true }) || item.value
        });
      });
    }

    // If they pass an array return it as is
    return (array.length) ? array : obj;
  }


  // Some attributes are hidden but we still need them for
  // calculating the price correctly. Visibility rules removes
  // them from the getRenderableAttributes function
  function getDataAttributes(overrideAllowed) {
    // Get all the attributes, including hidden _render attributes
    const allAttributes = app.helpers.getRenderableAttributes(true, function (item) {
      return (
        item.alias.indexOf('render') >= 0 ||
        item.canBeRendered ||
        item.alias.indexOf('watch') >= 0
      );
    });

    // Clean up the attribute list to remove unneeded data
    const attributes = _.omitBy(allAttributes, function (attr) {
      const alias = attr.alias,
        isAllowed = overrideAllowed ? (
          alias !== undefined &&
          alias.indexOf('_selector') >= 0 ||
          attr.selectorType === 'buttongroup'
        ) :
          (
            alias !== undefined &&
            (
              alias.indexOf('monogram') >= 0 ||
              alias.indexOf('_selector') >= 0 ||
              attr.selectorType === 'buttongroup'
            )
          );
      return isAllowed;
    });

    return attributes;
  }


  function setProgress(progress) {
    if (!app.mergedParams.configureLoaded) {
      $container.find('.fc-radial-progress').attr('data-progress', progress);

      if (progress === 100) {
        setTimeout(function () {
          $container.find('.fc-preloader-container ').fadeOut(function () {
            $(this).remove();
          });
          app.mergedParams.configureLoaded = true;
        }, 700);
      }
    }
  }

  function scrollToTop() {
    $('html, body').animate({ scrollTop: 0 }, 'slow');
  }

  const setDialogWidth = (width = 700) => configure.run('setComponentOptions', { 'dialog': { width } });

  const statusClass = (value) => {
    return `fc-customize-status-${value.toLowerCase()}`;
  };

  const setFieldsVisibility = (status) => {
    if (status === 'Yes') {
      $('.fc-disclaimer-container').removeClass('fc-hidden');
    } else {
      $('.fc-disclaimer-container').addClass('fc-hidden');
    }
  };

  function addCustomizeMobileHandlers(customizeItAlias = 'customize_selection') {
    if (!app.helpers.hasAttribute(customizeItAlias)) {
      return;
    }
    const status = app.helpers.getSelectedValueByAlias(customizeItAlias).vendorId;
    setFieldsVisibility(status);

    $('.fc-customize-it-selector').off('click').on('click', function () {
      const status = app.helpers.getSelectedValueByAlias(customizeItAlias).vendorId;
      $(this).removeClass(statusClass(status));
      const newStatus = status === 'Yes' ? 'No' : 'Yes';
      const isActive = newStatus === 'Yes';
      if (!isActive) {
        app.menu.component().trigger('attributeList:setIndex', 0);
        // Disable the next arrow when we disable customize it
        app.menu.navigationArrows.setNavigationArrowsStatus();
      }
      configure.run('selectValue', { ca: customizeItAlias, av: { vendorId: newStatus } }, (err) => {
        app._callback(err);
        $(this).addClass(statusClass(newStatus));
        setFieldsVisibility(newStatus);
        if (isActive) {
          app.menu.component().trigger('attributeList:setIndex', 1);
        }
        $(this).attr('aria-pressed', isActive);
        $('.fc-pager-page').find('.fc-outline-target')[0].focus();
      });
    });
  }

  function parseUrlParams() {
    // Return an object with keys/values from URL params
    const search = window.location.search.replace('?', '&').split('&');
    const obj = {};

    search.map(function (el) {
      if (el.length > 0) {
        const ary = el.split('=');
        obj[decodeURIComponent(ary[0])] = decodeURIComponent(ary[1]);
        return obj;
      } else {
        return {};
      }
    });

    return obj;
  };

  function dataURLtoBlob(dataURL) {
    //http://mitgux.com/send-canvas-to-server-as-file-using-ajax
    // Decode the dataURL
    const binary = atob(dataURL.split(',')[1]);
    // Create 8-bit unsigned array
    const array = [];

    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    // Return our Blob object
    return new Blob([new Uint8Array(array)], { type: 'image/png' });
  }

  function stringToBytesFaster(str) {
    //http://stackoverflow.com/questions/1240408/reading-bytes-from-a-javascript-string
    const re = [];
    let ch;
    let st;
    let j = 0;

    for (let i = 0; i < str.length; i++) {
      ch = str.charCodeAt(i);

      if (ch < 127) {
        re[j++] = ch & 0xFF;
      } else {
        st = []; // clear stack

        do {
          st.push(ch & 0xFF);  // push byte to stack
          ch = ch >> 8;          // shift value down by 1 byte
        } while (ch);
        // add stack contents to result
        // done because chars have "wrong" endianness
        st = st.reverse();

        for (let k = 0; k < st.length; ++k) {
          re[j++] = st[k];
        }
      }
    }
    // return an array of bytes
    return re;
  }

  return {
    checkPixelColor,
    getDataAttributes,
    getSelectedValues,
    hasAttribute,
    hasUiAlias,
    isNonClothingProduct,
    isCustomizeItProduct,
    isPrintAndEmbroideryProduct,
    isDTGProduct,
    isSimpleCYO,
    isDenimProduct,
    isCampaign,
    isRecipeValid,
    isJapan,
    isDefaultRecipe,
    scrollToTop,
    setColor,
    setDialogWidth,
    setProgress,
    transformObj,
    typography,
    addCustomizeMobileHandlers,
    setFieldsVisibility,
    parseUrlParams,
    dataURLtoBlob,
    stringToBytesFaster
  };
};

module.exports = AppUtils;
